import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

// 🔹 Reusable Try It Button
function TryItButton({ code }) {
  const handleClick = () => {
    const encodedCode = encodeURIComponent(code);
    window.open(`/editorJS?code=${encodedCode}`, "_blank");
  };

  return (
    <button onClick={handleClick} className="try_it_button">
      ▶ Try It
    </button>
  );
}

export default function JS_Syntax() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">🧩 JavaScript Syntax</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        The <b>syntax</b> of JavaScript defines how code is written and
        structured. It includes the rules for variables, statements, blocks, and
        comments. Understanding syntax helps you write error-free, readable
        JavaScript.
      </p>

      {/* Key Points */}
      <div
        className="Key_point_container"
        style={{
          background: "linear-gradient(135deg, #f0f9ff, #e0f2fe)",
        }}
      >
        <h2 style={{ color: "#0369a1" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            JavaScript is <b>case-sensitive</b>.
          </li>
          <li>
            Each instruction is a <b>statement</b>.
          </li>
          <li>
            Statements usually end with a semicolon (<code>;</code>).
          </li>
          <li>
            Code blocks are written inside <code>{`{ ... }`}</code>.
          </li>
          <li>Comments help explain code but are ignored during execution.</li>
        </ul>
      </div>

      {/* Example 1: Case Sensitivity */}
      <div className="tutorial-section">
        <h2 className="section-title">1️⃣ Case Sensitivity</h2>
        <p>
          JavaScript distinguishes between uppercase and lowercase letters. So,{" "}
          <code>name</code> and <code>Name</code> are two different variables.
        </p>
        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let Name = "Abhi";
let name = "John";
console.log(Name);
console.log(name);`}
          />
          <TryItButton
            code={`let Name = "Abhi";
let name = "John";
console.log(Name);
console.log(name);`}
          />
        </div>
        <div
          style={{
            marginTop: "1rem",
            fontSize: "1.2rem",
            padding: "10px",
            border: "2px dashed #3b82f6",
            borderRadius: "8px",
          }}
        >
          Output:
          <pre>
            Abhi
            <br />
            John
          </pre>
        </div>
      </div>

      {/* Example 2: Statements and Semicolons */}
      <div className="tutorial-section">
        <h2 className="section-title">2️⃣ Statements and Semicolons</h2>
        <p>
          JavaScript statements are actions the browser can perform. Semicolons
          separate statements but are often optional.
        </p>
        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let x = 5;
let y = 10
let total = x + y
console.log(total);`}
          />
          <TryItButton
            code={`let x = 5;
let y = 10
let total = x + y
console.log(total);`}
          />
        </div>
        <div
          style={{
            marginTop: "1rem",
            fontSize: "1.2rem",
            padding: "10px",
            border: "2px dashed #16a34a",
            borderRadius: "8px",
          }}
        >
          Output: <b>15</b>
        </div>
      </div>

      {/* Example 3: Code Blocks */}
      <div className="tutorial-section">
        <h2 className="section-title">3️⃣ Code Blocks</h2>
        <p>
          Code blocks group multiple statements inside curly braces{" "}
          <code>{`{}`}</code>. They are used in functions, loops, and
          conditionals.
        </p>
        <div className="code-demo-box">
          <CodeCopyBlock
            code={`{
  let message = "This is a block!";
  console.log(message);
}`}
          />
          <TryItButton
            code={`{
  let message = "This is a block!";
  console.log(message);
}`}
          />
        </div>
        <div
          style={{
            marginTop: "1rem",
            fontSize: "1.2rem",
            padding: "10px",
            border: "2px dashed #9333ea",
            borderRadius: "8px",
          }}
        >
          Output: <b>This is a block!</b>
        </div>
      </div>

      {/* Example 4: Comments */}
      <div className="tutorial-section">
        <h2 className="section-title">4️⃣ Comments</h2>
        <p>
          Comments are ignored by JavaScript and are used to explain code. You
          can write single-line or multi-line comments.
        </p>
        <div className="code-demo-box">
          <CodeCopyBlock
            code={`// This is a single-line comment
let x = 10; // comment at the end of line

/*
 This is a multi-line comment
 It can span several lines
*/
console.log(x);`}
          />
          <TryItButton
            code={`// This is a single-line comment
let x = 10; // comment at the end of line

/*
 This is a multi-line comment
 It can span several lines
*/
console.log(x);`}
          />
        </div>
        <div
          style={{
            marginTop: "1rem",
            fontSize: "1.2rem",
            padding: "10px",
            border: "2px dashed #f59e0b",
            borderRadius: "8px",
          }}
        >
          Output: <b>10</b>
        </div>
      </div>

      {/* Example 5: Whitespace and Line Breaks */}
      <div className="tutorial-section">
        <h2 className="section-title">5️⃣ Whitespace and Line Breaks</h2>
        <p>
          JavaScript ignores extra spaces and new lines, so use them to make
          your code more readable.
        </p>
        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let x    =    5;
let y=10;
let sum = x + y;
console.log( sum );`}
          />
          <TryItButton
            code={`let x    =    5;
let y=10;
let sum = x + y;
console.log( sum );`}
          />
        </div>
        <div
          style={{
            marginTop: "1rem",
            fontSize: "1.2rem",
            padding: "10px",
            border: "2px dashed #2563eb",
            borderRadius: "8px",
          }}
        >
          Output: <b>15</b>
        </div>
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Learn about <b>JavaScript Variables</b> to store and use data.
          </li>
          <li>
            Understand <b>Data Types</b> like strings, numbers, and booleans.
          </li>
          <li>
            Explore <b>Operators</b> to perform calculations and comparisons.
          </li>
        </ul>
      </div>
    </div>
  );
}
